import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type GroupElement, type MultiPageImageModel, type MultiPageVideoModel } from '@bynder-studio/render-core';
import { Badge, Divider, Form, ListItem, Select, Thumbnail } from '@bynder/design-system';
import { IconMove } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { Section } from '../../index.styled';

type DynamicElementSelectionProps = {
    selectedElement: GroupElement;
    creativeModel: MultiPageImageModel | MultiPageVideoModel;
    disabled: boolean;
};

const DynamicElementSelection = ({ selectedElement, creativeModel, disabled }: DynamicElementSelectionProps) => {
    const { translate } = useTranslate();
    const [searchValue, setSearchValue] = useState('');

    const isEnabled = selectedElement.onlyAllowOneSelection;

    const sortedChildren = useMemo(
        () => [...selectedElement.children].sort((a, b) => b.renderOrder - a.renderOrder),
        [selectedElement.children],
    );

    const activeChild = useMemo(() => {
        const found = sortedChildren.find((child) => child.id === selectedElement.activeDropdownElementId);

        return found || sortedChildren[0] || null;
    }, [selectedElement.activeDropdownElementId, sortedChildren]);

    // Auto-correct dangling activeDropdownElementId and disable DES when children are removed
    useEffect(() => {
        if (!isEnabled) {
            return;
        }

        // Disable DES if all children have been removed
        if (sortedChildren.length === 0) {
            creativeModel.updateElement(selectedElement.id, {
                onlyAllowOneSelection: false,
                activeDropdownElementId: null,
            });

            return;
        }

        const idIsDangling =
            selectedElement.activeDropdownElementId !== null &&
            !sortedChildren.some((child) => child.id === selectedElement.activeDropdownElementId);

        if (idIsDangling) {
            const fallbackId = sortedChildren[0]?.id ?? null;

            creativeModel.updateElement(selectedElement.id, {
                activeDropdownElementId: fallbackId,
            });
        }
    }, [isEnabled, selectedElement.activeDropdownElementId, selectedElement.id, sortedChildren, creativeModel]);

    const filteredChildren = useMemo(
        () =>
            sortedChildren.filter(
                (child) => !searchValue || child.name.toLowerCase().includes(searchValue.toLowerCase()),
            ),
        [sortedChildren, searchValue],
    );

    const handleToggle = useCallback(() => {
        if (isEnabled) {
            creativeModel.updateElement(selectedElement.id, {
                onlyAllowOneSelection: false,
                activeDropdownElementId: null,
            });
        } else {
            const firstChild = sortedChildren[0];

            creativeModel.updateElement(selectedElement.id, {
                onlyAllowOneSelection: true,
                activeDropdownElementId: firstChild?.id ?? null,
            });
        }
    }, [isEnabled, selectedElement, sortedChildren, creativeModel]);

    const handleSelectionChange = useCallback(
        (childId: number) => {
            if (childId === activeChild?.id) {
                return;
            }

            creativeModel.updateElement(selectedElement.id, {
                activeDropdownElementId: childId,
            });
        },
        [activeChild, selectedElement, creativeModel],
    );

    return (
        <>
            <Section aria-labelledby="shots-dynamic-element-selection">
                <Form onSubmit={preventSubmit}>
                    <Form.Group>
                        <Form.Label>
                            <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                {translate('editor.sidebar.shots.group.des.title')}
                                <Badge>{translate('editor.sidebar.shots.group.des.badge')}</Badge>
                            </span>
                        </Form.Label>
                        <ListItem
                            thumbnail={<Thumbnail icon={<IconMove />} />}
                            checkedVariant="switch"
                            isChecked={isEnabled}
                            isDisabled={disabled}
                            onClick={handleToggle}
                        >
                            {translate('editor.sidebar.shots.group.des.toggle')}
                        </ListItem>
                    </Form.Group>
                    {isEnabled && sortedChildren.length > 0 && (
                        <Form.Group>
                            <Form.Label>{translate('editor.sidebar.shots.group.des.active')}</Form.Label>
                            <Select
                                value={activeChild ? String(activeChild.id) : null}
                                onChange={(childId) => {
                                    if (childId !== null) {
                                        handleSelectionChange(Number(childId));
                                    }
                                }}
                                label={activeChild?.name ?? ''}
                                isDisabled={disabled}
                                aria-label={translate('editor.sidebar.shots.group.des.active')}
                            >
                                <Select.SearchInput
                                    value={searchValue}
                                    onChange={setSearchValue}
                                    placeholder={translate('editor.sidebar.shots.group.des.search')}
                                    aria-label={translate('editor.sidebar.shots.group.des.search')}
                                />
                                {filteredChildren.length > 0 ? (
                                    filteredChildren.map((child) => (
                                        <Select.Item key={child.id} value={String(child.id)}>
                                            {child.name}
                                        </Select.Item>
                                    ))
                                ) : (
                                    <Select.Text>{translate('editor.sidebar.shots.group.des.noResults')}</Select.Text>
                                )}
                            </Select>
                        </Form.Group>
                    )}
                </Form>
            </Section>
            <Divider />
        </>
    );
};

export default DynamicElementSelection;

function preventSubmit(e: React.FormEvent) {
    e.preventDefault();
}
